import './assets/main.css'

import { createApp } from 'vue'
import App from './App.vue'
import keycloakClient from '@/clients/keycloak.client';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import {initializeRouter} from './router';
import useAuthStore from "@/stores/auth.store";

//PRIME VUE COMPONENTS
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import StyleClass from "primevue/styleclass";
import Ripple from 'primevue/ripple';
import Toast from "primevue/toast";
import Tooltip from "primevue/tooltip";
import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';
import {ConfirmationService, ConfirmDialog, Select} from "primevue";

// Chart.js-Plugins gehören global registriert; sie werden erst aktiv, wo ein Chart sie in seinen
// Optionen konfiguriert (derzeit die Allokationsgüte-Plots).
import { Chart } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

Chart.register(zoomPlugin);

const Noir = definePreset(Aura, {
    semantic: {
        sonnnig: {
            background:{
                yellowLight: '{amber.100}',
                blue: '#F7F9FB',
                yellow: '{amber.400}'
            },
            error: '{red.500}'
        },
        primary: {
            50: '{amber.50}',
            100: '{amber.100}',
            200: '{amber.200}',
            300: '{amber.300}',
            400: '{amber.400}',
            500: '{amber.500}',
            600: '{amber.600}',
            700: '{amber.700}',
            800: '{amber.800}',
            900: '{amber.900}',
            950: '{amber.950}'
        },
        colorScheme: {
            light: {
                surface: {
                    0: '#ffffff',
                    50: '{slate.50}',
                    100: '{slate.100}',
                    200: '{slate.200}',
                    300: '{slate.300}',
                    400: '{slate.400}',
                    500: '{slate.500}',
                    600: '{slate.600}',
                    700: '{slate.700}',
                    800: '{slate.800}',
                    900: '{slate.900}',
                    950: '{slate.950}'
                },
                root: {
                    background: '#f8fafc',
                    color: '{surface.700}'
                },
                primary: {
                    color: '{amber.400}',
                    inverseColor: '#ffffff',
                    hoverColor: '{amber.500}',
                    activeColor: '{amber.600}'
                },
                highlight: {
                    background: '{blue.600}',
                    focusBackground: '{blue.700}',
                    color: '#ffffff',
                    focusColor: '#ffffff'
                },
                formField: {
                    background: '{surface.0}',
                    disabledBackground: '{surface.100}',
                    filledBackground: '{surface.50}',
                    filledFocusBackground: '{surface.0}',
                    borderColor: '{surface.300}',
                    hoverBorderColor: '{surface.400}',
                    focusBorderColor: '{primary.color}',
                    invalidBorderColor: '{red.400}',
                    color: '{surface.700}',
                    disabledColor: '{surface.500}',
                    placeholderColor: '{surface.300}',
                    floatLabelColor: '{surface.500}',
                    floatLabelFocusColor: '{surface.500}',
                    floatLabelInvalidColor: '{red.400}',
                    boxShadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(15, 23, 42, 0.05)'
                }
            }
        }
    },
    components: {
        button: {
            borderRadius: '8px',
            paddingX: '0.75rem',
            paddingY: '0.75rem',
            fontWeight: '600',
            transition: 'all 0.2s ease'
        },
        inputtext: {
            borderRadius: '8px',
            borderWidth: '2px',
            borderColor: '#e5e7eb',
            padding: '0.75rem',
            focusBorderColor: '#f59e0b',
            focusBoxShadow: '0 0 0 3px rgba(245, 158, 11, 0.1)'
        },
        dropdown: {
            borderRadius: '8px',
            borderWidth: '2px',
            borderColor: '#e5e7eb',
            padding: '0.75rem',
            focusBorderColor: '#f59e0b',
            focusBoxShadow: '0 0 0 3px rgba(245, 158, 11, 0.1)'
        },
        multiselect: {
            borderRadius: '8px',
            borderWidth: '2px',
            borderColor: '#e5e7eb',
            padding: '0.75rem',
            focusBorderColor: '#f59e0b',
            focusBoxShadow: '0 0 0 3px rgba(245, 158, 11, 0.1)'
        },
        tooltip: {
            maxWidth: '300px',
            padding: '0.75rem',
            borderRadius: '8px',
            fontSize: '0.875rem',
            lineHeight: '1.4'
        }
    }
});




// Create Pinia instance
const pinia = createPinia();

// Use persisted state with Pinia so our store data will persist even after page refresh
pinia.use(piniaPluginPersistedstate);

const renderApp = () => {
    const app = createApp(App);
    console.log("calling use router");
    app.use(PrimeVue, {
        // Default theme configuration
        ripple: true,
        theme: {
            preset: Noir,
            options: {
                prefix: 'p',
                darkModeSelector: false,
                //https://github.com/primefaces/primevue/issues/5540
                cssLayer: {
                    name: "primeui",
                    order: "tailwind-base, primeui, primevue, tailwind-utilities;"
                }
            }
        }
    });
    app.use(pinia);
    app.use(initializeRouter());
    app.use(ToastService);
    app.use(ConfirmationService);
    app.component('Toast', Toast);
    app.component('Select', Select);
    app.component('ConfirmDialog', ConfirmDialog);
    app.component('ToggleButton', () => import('primevue/togglebutton'));
    app.directive('ripple', Ripple);
    app.directive('styleclass', StyleClass);
    app.directive('tooltip', Tooltip);
    app.mount("#app");
}

keycloakClient.init(() => {}).then(
    () => {
        //we need to initialize auth store here cause we use it in routing
        //routing is started async before any setup functions are called
        //stores need to be initialized either in setup functions or with the pinia instance as parameters
        let store = useAuthStore(pinia);
        store.initStoreValues(keycloakClient.extractKeycloakInfo()).then(renderApp);
    }
);

